import React, { useState } from 'react';
import {
  CalendarIcon,
  UserGroupIcon
} from '@heroicons/react/24/outline';
import {
  BrandModal,
  BrandModalBody,
  BrandModalFooter,
  BrandModalHeader,
} from './ui/BrandModal';

interface ReportScheduleModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSchedule: (data: any) => void;
}

interface ReportSchedule {
  name: string;
  type: 'weekly' | 'monthly' | 'quarterly' | 'yearly' | 'cost-analysis';
  frequency: 'daily' | 'weekly' | 'monthly';
  format: 'pdf' | 'excel' | 'csv' | 'json';
  recipients: string[];
  location: string;
  enabled: boolean;
  time: string;
  dayOfWeek?: number;
  dayOfMonth?: number;
}

export default function ReportScheduleModal({
  isOpen,
  onClose,
  onSchedule
}: ReportScheduleModalProps) {
  const [scheduleData, setScheduleData] = useState<ReportSchedule>({
    name: '',
    type: 'weekly',
    frequency: 'weekly',
    format: 'pdf',
    recipients: [''],
    location: '',
    enabled: true,
    time: '08:00',
    dayOfWeek: 1
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      const cleanedSchedule = {
        ...scheduleData,
        recipients: scheduleData.recipients.filter(email => email.trim())
      };
      onSchedule(cleanedSchedule);
      onClose();
      
      // Reset form
      setScheduleData({
        name: '',
        type: 'weekly',
        frequency: 'weekly',
        format: 'pdf',
        recipients: [''],
        location: '',
        enabled: true,
        time: '08:00',
        dayOfWeek: 1
      });
      setErrors({});
    }
  };

  const handleAddRecipient = () => {
    setScheduleData(prev => ({
      ...prev,
      recipients: [...prev.recipients, '']
    }));
  };

  const handleRemoveRecipient = (index: number) => {
    setScheduleData(prev => ({
      ...prev,
      recipients: prev.recipients.filter((_, i) => i !== index)
    }));
  };

  const handleRecipientChange = (index: number, value: string) => {
    setScheduleData(prev => ({
      ...prev,
      recipients: prev.recipients.map((recipient, i) => 
        i === index ? value : recipient
      )
    }));
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!scheduleData.name.trim()) {
      newErrors.name = 'Schedule name is required';
    }

    if (scheduleData.recipients.filter(email => email.trim()).length === 0) {
      newErrors.recipients = 'At least one recipient is required';
    }

    const validEmails = scheduleData.recipients.filter(email => {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return email.trim() && emailRegex.test(email.trim());
    });

    if (validEmails.length === 0) {
      newErrors.recipients = 'At least one valid email address is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const reportTypes = [
    { value: 'weekly', label: 'Weekly Summary' },
    { value: 'monthly', label: 'Monthly Report' },
    { value: 'quarterly', label: 'Quarterly Analysis' },
    { value: 'yearly', label: 'Annual Report' },
    { value: 'cost-analysis', label: 'Cost Analysis' }
  ];

  const frequencies = [
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' }
  ];

  const formats = [
    { value: 'pdf', label: 'PDF Document' },
    { value: 'excel', label: 'Excel Spreadsheet' },
    { value: 'csv', label: 'CSV Data' },
    { value: 'json', label: 'JSON Data' }
  ];

  const daysOfWeek = [
    { value: 1, label: 'Monday' },
    { value: 2, label: 'Tuesday' },
    { value: 3, label: 'Wednesday' },
    { value: 4, label: 'Thursday' },
    { value: 5, label: 'Friday' },
    { value: 6, label: 'Saturday' },
    { value: 0, label: 'Sunday' }
  ];

  return (
    <BrandModal open={isOpen} onClose={onClose} maxWidthClass="max-w-md">
      <BrandModalHeader title="Schedule Report" onClose={onClose} />
      <form onSubmit={handleSubmit}>
        <BrandModalBody className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700">
                Report Type
              </label>              <select
                value={scheduleData.type}
                onChange={(e) => setScheduleData(prev => ({ ...prev, type: e.target.value as ReportSchedule['type'] }))}
                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm rounded-md"
              >
                {reportTypes.map(type => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700">
                Frequency
              </label>
              <select
                value={scheduleData.frequency}
                onChange={(e) => setScheduleData(prev => ({ ...prev, frequency: e.target.value as ReportSchedule['frequency'] }))}
                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm rounded-md"
              >
                {frequencies.map(freq => (
                  <option key={freq.value} value={freq.value}>{freq.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700">
                Format
              </label>
              <select
                value={scheduleData.format}
                onChange={(e) => setScheduleData(prev => ({ ...prev, format: e.target.value as ReportSchedule['format'] }))}
                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm rounded-md"
              >
                {formats.map(format => (
                  <option key={format.value} value={format.value}>{format.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Recipients
              </label>
              <div className="space-y-2">
                {scheduleData.recipients.map((recipient, index) => (
                  <div key={index} className="flex gap-2">
                    <input
                      type="email"
                      value={recipient}
                      onChange={(e) => handleRecipientChange(index, e.target.value)}
                      placeholder="email@example.com"
                      className="flex-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm"
                    />
                    {scheduleData.recipients.length > 1 && (
                      <button
                        type="button"
                        onClick={() => handleRemoveRecipient(index)}
                        className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  onClick={handleAddRecipient}
                  className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-cyan-700 bg-cyan-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500"
                >
                  <UserGroupIcon className="h-4 w-4 mr-2" />
                  Add Recipient
                </button>
              </div>
              {errors.recipients && <p className="mt-1 text-sm text-red-600">{errors.recipients}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700">
                Location Filter
              </label>
              <select
                value={scheduleData.location}
                onChange={(e) => setScheduleData(prev => ({ ...prev, location: e.target.value }))}
                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm rounded-md"
              >
                <option key="empty-option-1753461297340" value="">All Locations</option>
                <option key="Main Aviary-1753461297340" value="Main Aviary">Main Aviary</option>
                <option key="Breeding Area-1753461297340" value="Breeding Area">Breeding Area</option>
                <option key="Quarantine-1753461297340" value="Quarantine">Quarantine</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700">
                Time
              </label>
              <input
                type="time"
                value={scheduleData.time}
                onChange={(e) => setScheduleData(prev => ({ ...prev, time: e.target.value }))}
                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm rounded-md"
              />
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                checked={scheduleData.enabled}
                onChange={(e) => setScheduleData(prev => ({ ...prev, enabled: e.target.checked }))}
                className="h-4 w-4 text-cyan-600 focus:ring-cyan-500 border-gray-300 rounded"
              />
              <label className="ml-2 block text-sm text-slate-900">
                Enable Schedule
              </label>
            </div>

        </BrandModalBody>
        <BrandModalFooter>
          <button
            type="button"
            onClick={onClose}
            className="btn btn-secondary text-sm"
          >
            Cancel
          </button>
          <button type="submit" className="btn btn-primary text-sm inline-flex items-center">
            <CalendarIcon className="h-4 w-4 mr-2" />
            Schedule Report
          </button>
        </BrandModalFooter>
      </form>
    </BrandModal>
  );
} 